import { pageConfig } from '@/common/service/page-config';
import findDefaultPlan, { addToCartByPlanCode, addToCartByCategoryAndBillingCycle, de$type, planQueryParser } from '@/common/service/flow/utils';
import getPbxBillingCycle from '@/plans/ZOOM_PHONE/helper/pbx-billing-cycle.js';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { SMB_SUPPORT_ADD2CARD } from '@/common/service/non-standard-billing-cycle-utils';
import { isFromZeConsumption } from '@/plans/EVENT_SAAS/helper/utils';
import { handleRecommended } from '@/plans/CMR/constants';
import { webinarSingleUseAllowed } from '@/common/service/op-switch';
import _, { get } from 'lodash';
import {
  addBasePlanByTargetAddonPlan,
  addWebinarOneTimePsoRelatedPlan,
  getBasePlanInfo,
} from '@/common/product';
import { defaultRegion } from '@/plans/TOLLFREE/mixins/computed-mixins';
import { zoneBundleUtils } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import { getPbxCurrentBillingCycle } from '@/plans/ZOOM_PHONE/helper/pbx-billing-cycle';
import { computedPbxBasePlans, computedPbxNonBasePlans } from '@/common/service/computers/plans/pbx';
import {
  BILLING_CYCLE,
  planTypes,
  ZOOM_SASS_ADDITIONAL_CATEGORIES,
  BillingCycleEnum,
  AUDIO_SUB_TYPES,
  PBX_CATEGORYS,
  BASE_PLAN_SUB_TYPES,
  ZOOM_EDUCATION_INDIVIDUAL_PLAN_TYPES,
  ZOOM_EDUCATION_OPTIONAL_PLAN_TYPES,
  ZOOM_MEETING_EDU_PLUS_MIN_LICENSE,
} from '@/common/constants';
import { getBundleCategoryPlans } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import { plansof } from '@/utils/chain';
import {
  isEduSchCmpPlusContext,
  ZOOM_MEETING_SUB_TYPES
} from '@/plans/ZOOM_MEETING/common/utils';

const meetingTypes = [
  planTypes.ZOOM_MEETING,
  planTypes.ZOOM_ROOMS,
  planTypes.ZONE_EDU_PLUS,
  planTypes.ZOOM_ONE_BUNDLE,
  planTypes.ZONE_PRO_PLUS,
  planTypes.ZOOM_UNITED,
];

function getDependPlanTypeListByKey(planList, categoryCode, key = 'billingCycleDependBasePlanTypeList') {
  const __type = getPlanTypeByCategory(categoryCode);
  if (ZOOM_SASS_ADDITIONAL_CATEGORIES.includes(__type)) {
    return get(planList.find((item) => item.type === __type && item.category === categoryCode) || {}, key, []);
  }
  return get(planList.find((item) => item.type === __type) || {}, key, []);
};

const isSingleUseProFreeTrial = (boughtPlans) => {
  return boughtPlans.find((p) => p.isSingleUseProFreeTrial);
};

function build(getters, boughtPlans = [], planList = [], categoryCode, cmrUsedValue, plan) {

  function getDefaultBillingCycle(getters, boughtPlans, planList, categoryCode) {
    // SMB
    if (getters.basicConfig?.isSMBCustomer && SMB_SUPPORT_ADD2CARD) {
      const boughtBasePlan = getBasePlanInfo(boughtPlans);
      const basePlanBillingCycle = boughtBasePlan?.plans[0]?.billing_cycle;
      if (dependBillingCyclePlanTypeList.includes(boughtBasePlan?.planType) && BillingCycleEnum[basePlanBillingCycle]) {
        return basePlanBillingCycle
      }
    }

    // Pay per attendee
    // for zoom events/zoom saas consumption plan
    if (isFromZeConsumption()) {
      return BILLING_CYCLE.PAY_PER_ATTENDEE;
    }

    const dependBillingCyclePlanTypeList = getDependPlanTypeListByKey(planList, categoryCode);
    const _minCyclePlan = boughtPlans
    .filter((item) => dependBillingCyclePlanTypeList.includes(item.type))
    .sort((a, b) => a.cycleValue - b.cycleValue)[0];
    const queryPeriod = pageConfig?.period || BILLING_CYCLE.ANNUAL;
    if (_minCyclePlan) {
      const _minCycleValue = BillingCycleEnum[_minCyclePlan.billing_cycle];
      const queryPeriodValue = BillingCycleEnum[queryPeriod];
      return BillingCycleEnum[Math.min(_minCycleValue, queryPeriodValue)] || queryPeriod;
    }
    return queryPeriod;
  }
  
  function addDefaultPlanToCart(getters, targetBillingCycle, cmrUsedValue) {
    const targetType = getPlanTypeByCategory(categoryCode);
    const _typePlanList = planList.filter((p) => de$type(p.type) === targetType);
    let defaultPlan = null;
    // CMR
    if (
      (planTypes.CMR === targetType && getters.basicConfig.freeStorage) && 
      cmrUsedValue.overage > 0
    ) {
      defaultPlan = handleRecommended(_typePlanList, cmrUsedValue.overage);
    } else if (planTypes.TOLLFREE === targetType) {
      defaultPlan = planList.find(
        (item) =>
          item.type === planTypes.TOLLFREE &&
          item.category === AUDIO_SUB_TYPES.TOLL_FREE &&
          item.billing_cycle === BILLING_CYCLE.MONTHLY,
      );
      if (defaultPlan) {  defaultPlan.cartInfo.tollFreeProfileData = { ...defaultRegion } }
    } else if (planTypes.ZOOM_PHONE === targetType) {
      const { from, quantity = 1 } = pageConfig;
      const { category_codes = [], fromType } = planQueryParser(from || 'pbx_ZVM');
      const boughtPbxBasePlans = computedPbxBasePlans(boughtPlans);
      const billingCycle = getPbxBillingCycle(boughtPlans);

      // if user has pbx base plan
      if (boughtPbxBasePlans.length) {
        boughtPbxBasePlans.forEach((p) => {
          const plan = planList.find((_p) => p.plan_code === _p.plan_code);
          if (plan) {
            plan.cartInfo.selected_number = p.cartInfo.selected_number;
          }
        });
        category_codes.pop();
      }

      category_codes.forEach((category) =>
        addToCartByCategoryAndBillingCycle(
          planList,
          fromType,
          category,
          billingCycle,
          category === category_codes[0] ? quantity : 1,
        ),
      );
    } else {
      defaultPlan = findDefaultPlan({
        plans: _typePlanList.sort((a, b) => a.amount - b.amount),
        category: categoryCode,
        billingCycle: targetBillingCycle,
      });
    }
    defaultPlan && addToCartByPlanCode(planList, defaultPlan.plan_code, pageConfig.quantity || 1);
    return defaultPlan;
  }

  function addDependencePlan(boughtPlans = [], planList = [], targetPlan, plan) {
    const dependPlanTypeList = targetPlan.dependBasePlanTypeList;
    const boughtDependPlan = _.find(boughtPlans, (p) => _.includes(dependPlanTypeList, p.type));

    if (webinarSingleUseAllowed && targetPlan.type === planTypes.WEBINAR && isSingleUseProFreeTrial(boughtPlans)) {
      addWebinarOneTimePsoRelatedPlan(planList, targetPlan);
    }
    // from legacy edu pricing page
    else if (
      window.$context.is.edu &&
      [...ZOOM_EDUCATION_INDIVIDUAL_PLAN_TYPES, ...ZOOM_EDUCATION_OPTIONAL_PLAN_TYPES].includes(targetPlan.type)
    ) {
      // if bought meeting or zmr, don't need to add meeting edu
      const meetingLikePlan = boughtPlans.find((bp) => meetingTypes.includes(bp.type));
      if (!meetingLikePlan) {
        addBasePlanByTargetAddonPlan(planList, targetPlan, BASE_PLAN_SUB_TYPES.EDUCATION);
      }
    }
    // from new edu pricing page
    else if (getPlanTypeByCategory(plan) == planTypes.ZONE_EDU_PLUS || [ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP].includes(plan)) {
      addBasePlanByTargetAddonPlan(
        planList,
        targetPlan,
        boughtDependPlan?.type,
        BILLING_CYCLE.ANNUAL,
        getPlanTypeByCategory(plan),
        isEduSchCmpPlusContext && ZOOM_MEETING_EDU_PLUS_MIN_LICENSE,
        getPlanTypeByCategory(plan) === planTypes.ZOOM_MEETING && dependPlanTypeList?.includes(planTypes.ZOOM_MEETING) ? plan : ''
      );
    }
    // limit scope for case like 'increase with large meeting', add meetingSubCate as base plan
    else if ([ZOOM_MEETING_SUB_TYPES.BIZ].includes(plan) && targetPlan?.type == planTypes.LARGE_MEETING) {
      addBasePlanByTargetAddonPlan(
        planList,
        targetPlan,
        boughtDependPlan?.type,
        BILLING_CYCLE.ANNUAL,
        getPlanTypeByCategory(plan),
        0,
        dependPlanTypeList?.includes(planTypes.ZOOM_MEETING) ? plan : ''
      );
    }
    // default
    else {
      addBasePlanByTargetAddonPlan(planList, targetPlan, boughtDependPlan?.type);
    }
  }

  const targetBillingCycle = getDefaultBillingCycle(getters, boughtPlans, planList, categoryCode);
  const defaultPlan = addDefaultPlanToCart(getters, targetBillingCycle, cmrUsedValue);
  defaultPlan && addDependencePlan(boughtPlans, planList, defaultPlan, plan);
  return planList;
}

function buildForZoneBundle(planList, boughtPlans, targetPlanType = planTypes.ZOOM_ONE_BUNDLE) {
    // if has been purchased biz+, go to edit page
    const hasBundlePlan = plansof(boughtPlans).type(targetPlanType).has();
    if (hasBundlePlan) {
      const categoryPlanList = getBundleCategoryPlans(boughtPlans, planList, targetPlanType);
      categoryPlanList.forEach((p) => {
        addToCartByPlanCode(planList, p.plan_code, p.cartInfo.selected_number);
      });
      return planList;
    }
    const categoryPlanList = _.filter(planList, (p) => de$type(p.type) === targetPlanType);
    let plans = _.chain(categoryPlanList)
      .filter((x) => x.billing_cycle === BILLING_CYCLE.ANNUAL)
      .value();
    // HARDCODE
    let basePbxPlan = planList.find((p) => p.category === PBX_CATEGORYS.ZVP);
    const { plans: basePlans } = getBasePlanInfo(boughtPlans);
    const boughtPbxBasePlans = computedPbxBasePlans(boughtPlans);
    if (boughtPbxBasePlans?.[0]) {
      const boughtPbxPlans = computedPbxNonBasePlans(boughtPlans);
      basePbxPlan = boughtPbxBasePlans?.[0];
      const targetBillingCycle = getPbxCurrentBillingCycle(basePbxPlan, boughtPbxPlans?.[0]);
      plans = _.chain(categoryPlanList)
        .filter((x) => x.billing_cycle === targetBillingCycle)
        .value();
    } else if (basePlans?.[0]) {
      const billingCycle = basePlans?.[0]?.billing_cycle;
      plans = _.chain(categoryPlanList)
        .filter((x) => x.billing_cycle === billingCycle)
        .value();
    }
    if (plans.length === 0) {
      // If no any plan add annual plan as default
      plans = _.chain(categoryPlanList)
        .filter((x) => x.billing_cycle === BILLING_CYCLE.ANNUAL)
        .value();
    }
  
    let targetPlan = _.get(plans, [0]);
    if (targetPlan) {
      addToCartByPlanCode(planList, targetPlan.plan_code, zoneBundleUtils.getBundlePlanDefaultLicense(targetPlanType));
    }
    // load pbx base plan
    if (basePbxPlan) {
      addToCartByPlanCode(planList, basePbxPlan.plan_code, 1);
    }
    return planList;
}

function buildShoppingCart(getters, planList, boughtPlans, categoryCode, cmrUsedValue, plan) {
  const targetPlanType = getPlanTypeByCategory(categoryCode);
  if (zoneBundleUtils.isZoneBundleByPlanType(targetPlanType)) {
    return buildForZoneBundle(planList, boughtPlans, targetPlanType);
  }
  return build(getters, boughtPlans, planList, categoryCode, cmrUsedValue, plan);
}

function initShoppingCartByQuery(planList = [], boughtPlans = [], cmrUsedValue = {}, store) {
  const { plan = BASE_PLAN_SUB_TYPES.PRO, editConfig = {} } = pageConfig || {};
  editConfig.categories.forEach(
    categoryCode => buildShoppingCart(store.getters, planList, boughtPlans, categoryCode, cmrUsedValue, plan)
  );
  return planList;
}

export default initShoppingCartByQuery;
